import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import { ReactComponent as PlusSmallIcon } from '../../../icons/plus-small.svg';

interface ColumnDef {
  key: string;
  label: string;
  type?: string;
  options?: string[];
}

interface StackedDataSettingsProps {
  blockId: string;
  dataTableId: string | undefined;
  data: Record<string, unknown>;
  onSaved: (defaultView: 'table' | 'kanban') => void;
}

const KANBAN_COLORS: { name: string; hex: string }[] = [
  { name: 'red', hex: '#FBD0D9' },
  { name: 'green', hex: '#D2F9F4' },
  { name: 'yellow', hex: '#FFF7CC' },
  { name: 'blue', hex: '#D5E9F6' },
  { name: 'brown', hex: '#F7F3ED' },
  { name: 'grey', hex: '#838B9B' },
];

const COLUMN_TYPES: { value: string; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'enum', label: 'Options' },
];

const StackedDataSettings: React.FC<StackedDataSettingsProps> = ({
  blockId,
  dataTableId,
  data,
  onSaved,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const initialDefault = (data.defaultView as 'table' | 'kanban') || 'table';
  const groupByColumn = data.groupByColumn as string | undefined;

  const [columns, setColumns] = useState<ColumnDef[]>([]);
  const [columnsLoaded, setColumnsLoaded] = useState(false);

  const [defaultView, setDefaultView] = useState<'table' | 'kanban'>(
    initialDefault,
  );
  const [groupBy, setGroupBy] = useState<string>(groupByColumn || '');
  const [titleField, setTitleField] = useState<string>(
    (data.titleField as string) || '',
  );
  const [visibleFields, setVisibleFields] = useState<string[]>(
    (data.visibleFields as string[]) || [],
  );
  const [hideUnknown, setHideUnknown] = useState<boolean>(
    (data.hideUnknownColumn as boolean) || false,
  );
  const [colColors, setColColors] = useState<Record<string, string>>(
    (data.columnColors as Record<string, string>) || {},
  );

  // Add column form
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [newColLabel, setNewColLabel] = useState('');
  const [newColType, setNewColType] = useState('text');
  const [newColOptions, setNewColOptions] = useState<string[]>([]);
  const [newOptionInput, setNewOptionInput] = useState('');

  // Edit enum options
  const [editingEnumCol, setEditingEnumCol] = useState<string | null>(null);
  const [enumOptions, setEnumOptions] = useState<string[]>([]);
  const [enumOptionInput, setEnumOptionInput] = useState('');

  const [saving, setSaving] = useState(false);

  // Fetch columns on mount
  useEffect(() => {
    if (!dataTableId || columnsLoaded) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get(`/data-tables/${dataTableId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.success && res.data.dataTable) {
          const cols: ColumnDef[] = Array.isArray(res.data.dataTable.headings)
            ? res.data.dataTable.headings
            : [];
          setColumns(cols);
          setColumnsLoaded(true);

          // Auto-select defaults when no saved values exist
          const gb = groupByColumn || '';
          const available = cols.filter((c) => c.key !== gb);
          const savedTitle = data.titleField as string | undefined;
          const savedVisible = data.visibleFields as string[] | undefined;
          if (!savedTitle && available.length > 0) {
            setTitleField(available[0].key);
          }
          if (
            (!savedVisible || savedVisible.length === 0) &&
            available.length > 1
          ) {
            const tf = savedTitle || available[0].key;
            setVisibleFields(
              available.filter((c) => c.key !== tf).map((c) => c.key),
            );
          }
          if (!groupByColumn) {
            const firstEnum = cols.find((c) => c.type === 'enum');
            if (firstEnum) setGroupBy(firstEnum.key);
          }
        }
      } catch (err) {
        console.error('[StackedDataSettings] Failed to fetch columns:', err);
      }
    })();
  }, [
    dataTableId,
    columnsLoaded,
    getAccessTokenSilently,
    groupByColumn,
    data.titleField,
    data.visibleFields,
  ]);

  const persistBlockData = useCallback(
    async (updates: Record<string, unknown>) => {
      setSaving(true);
      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/project-apps/blocks/${blockId}/data`,
          { data: { ...data, ...updates } },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('[StackedDataSettings] Failed to save block data:', err);
      } finally {
        setSaving(false);
      }
    },
    [blockId, data, getAccessTokenSilently],
  );

  const persistHeadings = useCallback(
    async (updatedColumns: ColumnDef[]) => {
      if (!dataTableId) return;
      setSaving(true);
      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/data-tables/${dataTableId}`,
          { headings: updatedColumns },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setColumns(updatedColumns);
      } catch (err) {
        console.error('[StackedDataSettings] Failed to save headings:', err);
      } finally {
        setSaving(false);
      }
    },
    [dataTableId, getAccessTokenSilently],
  );

  const handleSave = useCallback(() => {
    persistBlockData({
      defaultView,
      groupByColumn: groupBy,
      titleField,
      visibleFields,
      hideUnknownColumn: hideUnknown,
      columnColors: colColors,
    });
    onSaved(defaultView);
  }, [
    defaultView,
    groupBy,
    titleField,
    visibleFields,
    hideUnknown,
    colColors,
    persistBlockData,
    onSaved,
  ]);

  const handleAddColumn = useCallback(() => {
    if (!newColLabel.trim()) return;
    const key = newColLabel
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    if (columns.some((c) => c.key === key)) return;

    const colDef: ColumnDef = {
      key,
      label: newColLabel.trim(),
      type: newColType,
    };
    if (newColType === 'enum' && newColOptions.length > 0) {
      colDef.options = newColOptions;
    }
    persistHeadings([...columns, colDef]);
    setNewColLabel('');
    setNewColType('text');
    setNewColOptions([]);
    setNewOptionInput('');
    setShowAddColumn(false);
  }, [newColLabel, newColType, newColOptions, columns, persistHeadings]);

  const openEnumEditor = useCallback(
    (colKey: string) => {
      const col = columns.find((c) => c.key === colKey);
      setEditingEnumCol(colKey);
      setEnumOptions(col?.options || []);
      setEnumOptionInput('');
    },
    [columns],
  );

  const handleSaveEnumOptions = useCallback(() => {
    if (!editingEnumCol) return;
    const updated = columns.map((c) =>
      c.key === editingEnumCol ? { ...c, options: enumOptions } : c,
    );
    persistHeadings(updated);
    setEditingEnumCol(null);
  }, [editingEnumCol, enumOptions, columns, persistHeadings]);

  const enumColumns = useMemo(
    () => columns.filter((c) => c.type === 'enum'),
    [columns],
  );
  const nonGroupByCols = useMemo(
    () => columns.filter((c) => c.key !== groupBy),
    [columns, groupBy],
  );

  return (
    <div className='stacked-data-settings'>
      {/* Default View */}
      <div className='sd-settings-section'>
        <h3 className='sd-settings-heading'>Default View</h3>
        <div className='sd-toggle-group'>
          <button
            className={`sd-toggle-btn${defaultView === 'table' ? ' active' : ''}`}
            onClick={() => setDefaultView('table')}
          >
            Table
          </button>
          <button
            className={`sd-toggle-btn${defaultView === 'kanban' ? ' active' : ''}`}
            onClick={() => setDefaultView('kanban')}
          >
            Board
          </button>
        </div>
      </div>

      {/* Group By Column */}
      <div className='sd-settings-section'>
        <h3 className='sd-settings-heading'>Group By Column</h3>
        {enumColumns.length === 0 ? (
          <p className='sd-hint'>No Select columns available. Add one first.</p>
        ) : (
          <div className='sd-radio-list'>
            {enumColumns.map((col) => (
              <div key={col.key} className='sd-enum-column-group'>
                <label className='sd-radio-option'>
                  <input
                    type='radio'
                    name='sd-group-by'
                    checked={groupBy === col.key}
                    onChange={() => setGroupBy(col.key)}
                  />
                  {col.label}
                  <button
                    className='sd-edit-enum-btn'
                    onClick={(e) => {
                      e.preventDefault();
                      if (editingEnumCol === col.key) {
                        setEditingEnumCol(null);
                      } else {
                        openEnumEditor(col.key);
                      }
                    }}
                  >
                    {editingEnumCol === col.key ? 'Done' : 'Edit options'}
                  </button>
                </label>
                {editingEnumCol === col.key && (
                  <div className='sd-inline-enum-editor'>
                    <div className='sd-enum-tags'>
                      {enumOptions.map((opt, i) => (
                        <span key={i} className='sd-enum-tag'>
                          {opt}
                          <button
                            type='button'
                            className='sd-enum-tag-remove'
                            onClick={() =>
                              setEnumOptions((prev) =>
                                prev.filter((_, idx) => idx !== i),
                              )
                            }
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                      <input
                        type='text'
                        className='sd-enum-input'
                        value={enumOptionInput}
                        onChange={(e) => setEnumOptionInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            (e.key === 'Enter' || e.key === ',') &&
                            enumOptionInput.trim()
                          ) {
                            e.preventDefault();
                            const val = enumOptionInput.trim();
                            if (!enumOptions.includes(val)) {
                              setEnumOptions((prev) => [...prev, val]);
                            }
                            setEnumOptionInput('');
                          }
                        }}
                        placeholder='Type an option and press Enter'
                        autoFocus
                      />
                    </div>
                    <div className='sd-form-actions'>
                      <button
                        className='sd-btn-primary'
                        onClick={handleSaveEnumOptions}
                      >
                        Save Options
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hide Unknown Column */}
      <div className='sd-settings-section'>
        <label className='sd-checkbox-option'>
          <input
            type='checkbox'
            checked={hideUnknown}
            onChange={(e) => setHideUnknown(e.target.checked)}
          />
          Hide &ldquo;Unknown&rdquo; column on Board
        </label>
      </div>

      {/* Column Colours */}
      {enumColumns.length > 0 && (
        <div className='sd-settings-section'>
          <h3 className='sd-settings-heading'>Column Colours</h3>
          <div className='sd-color-list'>
            {(() => {
              const groupByCol = enumColumns.find((c) => c.key === groupBy);
              const opts = groupByCol?.options || [];
              return opts.map((opt) => (
                <div key={opt} className='sd-color-row'>
                  <span className='sd-color-label'>{opt}</span>
                  <div className='sd-color-swatches'>
                    {KANBAN_COLORS.map((c) => (
                      <button
                        key={c.name}
                        className={`sd-color-swatch${colColors[opt] === c.hex ? ' active' : ''}`}
                        style={{ backgroundColor: c.hex }}
                        onClick={() =>
                          setColColors((prev) => ({ ...prev, [opt]: c.hex }))
                        }
                        title={c.name}
                      />
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Card Fields — two-column grid */}
      <div className='sd-card-fields-row'>
        <div className='sd-settings-section'>
          <h3 className='sd-settings-heading'>Card Title Field</h3>
          {nonGroupByCols.length === 0 ? (
            <p className='sd-hint'>No columns available.</p>
          ) : (
            <div className='sd-radio-list'>
              {nonGroupByCols.map((col) => (
                <label key={col.key} className='sd-radio-option'>
                  <input
                    type='radio'
                    name='sd-title-field'
                    checked={titleField === col.key}
                    onChange={() => setTitleField(col.key)}
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className='sd-settings-section'>
          <h3 className='sd-settings-heading'>Visible Card Fields</h3>
          {nonGroupByCols.filter((c) => c.key !== titleField).length === 0 ? (
            <p className='sd-hint'>No additional fields.</p>
          ) : (
            <div className='sd-checkbox-list'>
              {nonGroupByCols
                .filter((c) => c.key !== titleField)
                .map((col) => (
                  <label key={col.key} className='sd-checkbox-option'>
                    <input
                      type='checkbox'
                      checked={visibleFields.includes(col.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setVisibleFields((prev) => [...prev, col.key]);
                        } else {
                          setVisibleFields((prev) =>
                            prev.filter((k) => k !== col.key),
                          );
                        }
                      }}
                    />
                    {col.label}
                  </label>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Columns */}
      <div className='sd-settings-section'>
        <h3 className='sd-settings-heading'>Columns</h3>
        <div className='sd-column-list'>
          {columns.map((col) => (
            <span key={col.key} className='sd-column-tag'>
              {col.label}
              <span className='sd-column-type'>
                {col.type === 'enum' ? 'options' : col.type || 'text'}
              </span>
            </span>
          ))}
        </div>
        {!showAddColumn ? (
          <button
            className='sd-add-column-btn'
            onClick={() => setShowAddColumn(true)}
          >
            <PlusSmallIcon className='sd-plus-icon' />
            Add Column
          </button>
        ) : (
          <div className='sd-add-column-form'>
            <div className='sd-field'>
              <label>Name</label>
              <input
                type='text'
                value={newColLabel}
                onChange={(e) => setNewColLabel(e.target.value)}
                placeholder='e.g. Revenue'
                autoFocus
              />
            </div>
            <div className='sd-field'>
              <label>Type</label>
              <div className='sd-type-options'>
                {COLUMN_TYPES.map((t) => (
                  <button
                    key={t.value}
                    className={`sd-type-option${newColType === t.value ? ' active' : ''}`}
                    onClick={() => setNewColType(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            {newColType === 'enum' && (
              <div className='sd-field'>
                <label>Options</label>
                <div className='sd-enum-tags'>
                  {newColOptions.map((opt, i) => (
                    <span key={i} className='sd-enum-tag'>
                      {opt}
                      <button
                        type='button'
                        className='sd-enum-tag-remove'
                        onClick={() =>
                          setNewColOptions((prev) =>
                            prev.filter((_, idx) => idx !== i),
                          )
                        }
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                  <input
                    type='text'
                    className='sd-enum-input'
                    value={newOptionInput}
                    onChange={(e) => setNewOptionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        (e.key === 'Enter' || e.key === ',') &&
                        newOptionInput.trim()
                      ) {
                        e.preventDefault();
                        const val = newOptionInput.trim();
                        if (!newColOptions.includes(val)) {
                          setNewColOptions((prev) => [...prev, val]);
                        }
                        setNewOptionInput('');
                      }
                    }}
                    placeholder={
                      newColOptions.length === 0
                        ? 'Type an option and press Enter'
                        : 'Add another…'
                    }
                  />
                </div>
              </div>
            )}
            <div className='sd-form-actions'>
              <button
                className='sd-btn-primary'
                onClick={handleAddColumn}
                disabled={!newColLabel.trim()}
              >
                Add Column
              </button>
              <button
                className='sd-btn-secondary'
                onClick={() => {
                  setShowAddColumn(false);
                  setNewColLabel('');
                  setNewColType('text');
                  setNewColOptions([]);
                  setNewOptionInput('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Save */}
      <div className='sd-settings-footer'>
        <button className='sd-btn-primary' onClick={handleSave}>
          Save Settings
        </button>
        {saving && <span className='sd-save-indicator'>Saving…</span>}
      </div>
    </div>
  );
};

export default StackedDataSettings;
